import React, { useEffect, useState, useContext, useMemo } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import {
  OptionsMenu,
  Button,
  NotificationContext,
  ResponsiveTableView,
} from "@solsys/solsys-reactjs";
import { FaPlus, FaCog } from "react-icons/fa";
import RegionDropDown from "../../dropdowns/RegionDropDown";

const gqlQuery = gql`
  query userList($user_list_request: user_list_request) {
    user(user_list_request: $user_list_request) {
      edges {
        id
        username
        display_name
        email
        enabled
        app_role {
          name
        }
        user2region {
          edges {
            region {
              name
            }
          }
        }
      }
      totalCount
    }
  }
`;

export const ListOptions = {
  roleFilter: "roleFilter",
  regionFilter: "regionFilter",
  showDisabled: "showDisabled",
  search: "search",
};

function mapUserRow(u) {
  const regionNames = (u.user2region?.edges ?? [])
    .map((edge) => edge.region?.name)
    .filter(Boolean);
  return {
    id: u.id,
    username: u.username,
    display_name: u.display_name,
    email: u.email,
    role: u.app_role?.name ?? null,
    regions: regionNames.length > 0 ? regionNames.join(", ") : "—",
    enabled: u.enabled === null || u.enabled === undefined ? true : Boolean(u.enabled),
  };
}

export default function UserList({ id: entityId, action, ...props }) {
  const { addNotification } = useContext(NotificationContext);
  const isAdd = action === "add";

  const [requestState, setRequestState] = props.state;
  const setListOptions = (state) =>
    setRequestState({ ...requestState, ...state });

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState(requestState.roleFilter || "");
  const [regionFilter, setRegionFilter] = useState(requestState.regionFilter || null);
  const [showDisabled, setShowDisabled] = useState(Boolean(requestState.showDisabled));
  const [appliedFilters, setAppliedFilters] = useState({
    roleFilter: requestState.roleFilter || "",
    regionFilter: requestState.regionFilter || null,
    showDisabled: Boolean(requestState.showDisabled),
    search: requestState.search || "",
  });
  const isDisabledUser = (u) => u?.enabled === false;

  const [fetchUsers] = useLazyQuery(gqlQuery, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const edges = data?.user?.edges ?? [];
      setList(edges.map(mapUserRow));
      setLoading(false);
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", error.message || "Failed to load users");
      setList([]);
      setLoading(false);
    },
  });

  const listVariables = useMemo(() => {
    const filters = [];
    if (appliedFilters.roleFilter) {
      filters.push({
        field: "role",
        operator: "=",
        value: appliedFilters.roleFilter,
      });
    }
    if (appliedFilters.regionFilter) {
      filters.push({
        field: "region",
        operator: "=",
        value: String(appliedFilters.regionFilter),
      });
    }
    if (!appliedFilters.showDisabled) {
      filters.push({
        field: "enabled_active",
        operator: "=",
        value: "true",
      });
    }
    if (appliedFilters.search?.trim()) {
      filters.push({
        field: "search",
        operator: "like",
        value: appliedFilters.search.trim(),
      });
    }
    return {
      user_list_request: {
        filters,
        pagination: { limit: 10000 },
        sorting: [{ field: "id", direction: "DESC" }],
      },
    };
  }, [appliedFilters]);

  const runFetch = () => {
    setLoading(true);
    fetchUsers({ variables: listVariables });
  };

  useEffect(() => {
    runFetch();
  }, [listVariables]);

  useEffect(() => {
    if (action === "list") runFetch();
  }, [action]);

  useEffect(() => {
    if (props.onStateChange) props.onStateChange(requestState);
  }, [requestState]);

  const applyOptions = () => {
    setAppliedFilters({
      roleFilter: roleFilter || "",
      regionFilter: regionFilter || null,
      showDisabled,
      search: requestState.search || "",
    });
    setListOptions({
      roleFilter: roleFilter || undefined,
      regionFilter: regionFilter || undefined,
      showDisabled: showDisabled || undefined,
    });
    setIsFilterMenuOpen(false);
  };

  const setSearch = (value) => {
    const nextSearch = value;
    setAppliedFilters((prev) => ({ ...prev, search: nextSearch }));
    setListOptions({ search: nextSearch || undefined });
  };

  const resetOptions = () => {
    setRoleFilter("");
    setRegionFilter(null);
    setShowDisabled(false);
    setAppliedFilters({ roleFilter: "", regionFilter: null, showDisabled: false, search: "" });
    setListOptions({ roleFilter: undefined, regionFilter: undefined, showDisabled: undefined, search: undefined });
  };

  return (
    <OptionsMenu isOpen={isFilterMenuOpen}>
      <OptionsMenu.Menu>
        <div className="options-section">
          <h3>Filter by role</h3>
          <div className="option">
            <label>Role:</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="administrator">Administrator</option>
              <option value="customer">Customer</option>
              <option value="technician">Technician</option>
            </select>
          </div>
          <div className="option">
            <label>Region:</label>
            <RegionDropDown
              selected={regionFilter}
              onSelect={setRegionFilter}
              searchEnabled
            />
          </div>
          <div className="option">
            <label>
              <input
                type="checkbox"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Show disabled users
            </label>
          </div>
        </div>
        <div className="options-tools">
          <Button onClick={applyOptions} loading={loading}>Apply</Button>
          <button onClick={resetOptions}>Reset</button>
          <button onClick={() => setIsFilterMenuOpen(false)}>Close</button>
        </div>
      </OptionsMenu.Menu>
      <OptionsMenu.Layout>
        <div className="page-header">
          <span className="page-header-title">Users</span>
          <div className="page-header-options">
            {props.isMaster && (
              <button onClick={() => props.onSelect(0)}>
                <FaPlus />
              </button>
            )}
            <button onClick={() => setIsFilterMenuOpen(true)}>
              <FaCog />
            </button>
          </div>
        </div>
        <input
          value={ requestState.search ?? "" }
          style={ { width: "100%" } }
          placeholder="Search ID, username, display name, or email..."
          type="text"
          onChange={ (e) => setSearch(e.target.value) }
        />
        <ResponsiveTableView
          style={{ height: "100%" }}
          loading={loading}
          headers={[
            { label: "ID" },
            { label: "Username" },
            { label: "Display name" },
            { label: "Role" },
            { label: "Regions" },
            { label: "Status" },
          ]}
          data={list.map((u) => {
            const uid = u.id ?? u._id;
            const disabled = isDisabledUser(u);
            return {
              id: uid,
              ID: uid,
              Username: u.username || "—",
              "Display name": u.display_name || u.name || "—",
              Role: u.role || "—",
              Regions: u.regions || "—",
              Status: disabled ? "Disabled" : "Active",
              selected: String(uid) === String(entityId) && !isAdd,
            };
          })}
          identityColumnKey="ID"
          onRowClick={(row) => props.onSelect(row.id, true)}
          totalCount={list.length}
          addMoreResults={() => {}}
        />
      </OptionsMenu.Layout>
    </OptionsMenu>
  );
}
